import {
  Box,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  alpha,
} from '@mui/material';
import { ExpandMore, HelpOutline } from '@mui/icons-material';
import { themeColors } from '../../../theme/theme';
import { useTranslation } from '../../../kit/useTranslation';

export default function FAQSection({
  faqData,
  searchQuery,
  expanded,
  onAccordionChange,
  isDesktop = false,
}) {
  const { t } = useTranslation();
  // Filter FAQs based on search query
  const filteredFAQs = faqData
    .map((category) => ({
      ...category,
      questions: category.questions.filter(
        (faq) =>
          faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.a.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((category) => category.questions.length > 0);

  return (
    <Box sx={{ px: isDesktop ? 0 : 2 }}>
      {!isDesktop && (
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('support.faq')}
        </Typography>
      )}

      {filteredFAQs.length > 0 ? (
        <Stack spacing={3}>
          {filteredFAQs.map((category, catIndex) => (
            <Box key={catIndex}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha(themeColors.primaryColor, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <category.icon sx={{ color: themeColors.primaryColor, fontSize: 18 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  {category.category}
                </Typography>
              </Stack>

              {category.questions.map((faq, qIndex) => (
                <Accordion
                  key={`${catIndex}-${qIndex}`}
                  expanded={expanded === `${catIndex}-${qIndex}`}
                  onChange={onAccordionChange(`${catIndex}-${qIndex}`)}
                  elevation={0}
                  sx={{
                    mb: 1,
                    borderRadius: '12px !important',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      borderColor: '#6366f1',
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.1)',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: '#64748b' }} />}
                    sx={{
                      '& .MuiAccordionSummary-content': { my: 1.5 },
                      '&:hover': { bgcolor: '#f8fafc' },
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#334155' }}>
                      {faq.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.7 }}>
                      {faq.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ))}
        </Stack>
      ) : (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            textAlign: 'center',
            py: 6,
            bgcolor: '#f8fafc',
          }}
        >
          <HelpOutline sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
          <Typography sx={{ color: '#475569', fontWeight: 600 }} gutterBottom>
            {t('support.noResults', { query: searchQuery })}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {t('support.tryDifferent')}
          </Typography>
        </Card>
      )}
    </Box>
  );
}
